import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  GeneralCaretLeftIcon,
  GeneralCaretRightIcon,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import React, { useState } from 'react';

import RvHalfImage from '@/assets/group-rv-bookings/airstream-open-door-chairs.jpg';
import AlyshaSign from '@/assets/group-rv-bookings/Alysha.png';
import staffRoom from '@/assets/group-rv-bookings/cameraman-city-street.jpg';
import CommaOpen from '@/assets/group-rv-bookings/comma_open.png';
import JenniferSign from '@/assets/group-rv-bookings/Jennifer.png';
import FirstBgImage from '@/assets/group-rv-bookings/lake-dock-pink-sunset.jpg';
import MarkSign from '@/assets/group-rv-bookings/Mark.png';
import SmallCameraImage from '@/assets/group-rv-bookings/red-camera-desert-shoot.jpg';
import SinkImage from '@/assets/group-rv-bookings/rv-interior-living-area.jpg';
import staffCamera from '@/assets/group-rv-bookings/rv-interior-living-area.jpg';
import MeetHostImage from '@/assets/group-rv-bookings/wedding-couple-forest-aisle.jpg';
import FirstCardImage from '@/assets/group-rv-bookings/wyldwood-ranch-sign.jpg';
import MaxWidth from '@/components/common/layout/MaxWidth';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';

type TTestimonial = {
  key: string;
  bgImage: { src: string };
  cardImage: { src: string };
  quote: string;
  subtext: string;
  nameAndTitle: string;
  signature: { src: string };
};

type TGroupRvTestimonialTemplateType = 'LandingPage' | 'SubPage';

type TGroupRvTestimonialProps = {
  templateType?: TGroupRvTestimonialTemplateType;
  /**
   * For SubPage layout only: which testimonial content to show.
   * Defaults to 'wedding-coordinator'.
   */
  subPageKey?: TTestimonial['key'];
  isHomePage?: boolean;
  /**
   * For SubPage layout only: overrides the left-side image collage.
   * `primary` is the large image, `secondary` is the smaller overlapping image.
   */
  subPageImages?: { primary: { src: string }; secondary: { src: string } };
};

const TESTIMONIALS: TTestimonial[] = [
  {
    key: 'event-coordinator',
    bgImage: FirstBgImage,
    cardImage: FirstCardImage,
    quote:
      'Our concierge and hosts provided such a great experience. We would totally do this again!',
    subtext: 'Booked 20 RVs for a July 4th event in Texas',
    nameAndTitle: 'Mark V., Event Coordinator',
    signature: MarkSign,
  },
  {
    key: 'wedding-coordinator',
    bgImage: RvHalfImage,
    cardImage: MeetHostImage,
    quote:
      'Having all the RVs ready for the wedding guests made everyone feel like a VIP. Already sharing this idea with more brides!',
    subtext: 'Booked 20 RVs for a wedding in SANTA ROSA, CA',
    nameAndTitle: 'Jennifer S., Wedding Coordinator',
    signature: JenniferSign,
  },
  {
    key: 'staff-housing-coordinator',
    bgImage: RvHalfImage,
    cardImage: MeetHostImage,
    quote:
      'Our concierge Casey made having multiple RVs for a a fast-paced multi-day production seamless.',
    subtext: '',
    nameAndTitle: 'Sr. Production Coordinator who booked 5 RVs in Hollywood, FL',
    signature: AlyshaSign,
  },
  {
    key: 'production-coordinator',
    bgImage: SinkImage,
    cardImage: SmallCameraImage,
    quote:
      'Our concierge Casey made having multiple RVs for a fast-paced multi-day production seamless.',
    subtext: 'Booked 5 RVs for a TV PRODUCTION crew in HOLLYWOOD, FL',
    nameAndTitle: 'Alysha K., Sr. Production Coordinator',
    signature: AlyshaSign,
  },
];
const TESTIMONIALS2: TTestimonial[] = [
  {
    key: 'event-coordinator',
    bgImage: FirstBgImage,
    cardImage: FirstCardImage,
    quote:
      'Our concierge and hosts provided such a great experience. We would totally do this again!',
    subtext: 'Booked 20 RVs for a July 4th event in Texas',
    nameAndTitle: 'Mark V., Event Coordinator',
    signature: MarkSign,
  },
  {
    key: 'wedding-coordinator',
    bgImage: RvHalfImage,
    cardImage: MeetHostImage,
    quote:
      'Having all the RVs ready for the wedding guests made everyone feel like a VIP. Already sharing this idea with more brides!',
    subtext: 'Booked 20 RVs for a wedding in SANTA ROSA, CA',
    nameAndTitle: 'Jennifer S., Wedding Coordinator',
    signature: JenniferSign,
  },

  {
    key: 'staff-housing-coordinator',
    bgImage: staffCamera,
    cardImage: staffRoom,
    quote:
      'Our concierge Casey made having multiple RVs for a a fast-paced multi-day production seamless.',
    subtext: '',
    nameAndTitle: 'Sr. Production Coordinator who booked 5 RVs in Hollywood, FL',
    signature: AlyshaSign,
  },
];

export const GroupRvTestimonial: React.FC<TGroupRvTestimonialProps> = ({
  templateType = 'LandingPage',
  subPageKey = 'wedding-coordinator',
  subPageImages,
  isHomePage = false,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const active = (
    templateType === 'SubPage'
      ? TESTIMONIALS.find(t => t.key === subPageKey) || TESTIMONIALS[0]
      : TESTIMONIALS2[activeIndex]
  ) as TTestimonial;

  const goPrev = () => {
    setActiveIndex(prev => (prev - 1 + 3) % 3);
  };

  const goNext = () => {
    setActiveIndex(prev => (prev + 1) % 3);
  };

  return (
    <section
      className={`relative overflow-hidden bg-canvas-100 ${isHomePage ? '' : 'lg:py-20 py-12'}`}>
      <article className="relative flex justify-center overflow-hidden lg:min-h-[520px]">
        {/* Background image for current testimonial (LandingPage desktop only) */}
        {templateType === 'LandingPage' && (
          <div className="absolute inset-y-0 left-0 z-0 hidden w-[39%] lg:block">
            <ResponsiveImage
              src={active.bgImage.src}
              alt={active.nameAndTitle}
              className="object-cover w-full h-full"
            />
          </div>
        )}

        {/* Card image — absolutely positioned at bg/cream boundary (LandingPage desktop only) */}
        {templateType === 'LandingPage' && (
          <div
            className="absolute z-20 hidden overflow-hidden shadow-lg rounded-2xl lg:block left-[39%] top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ width: 240, height: 320 }}>
            <ResponsiveImage
              src={active.cardImage.src}
              alt={active.nameAndTitle}
              className="object-cover w-full h-full rounded-2xl"
            />
          </div>
        )}

        <div className="relative z-10 flex justify-center w-full">
          <MaxWidth className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-4">
            {/* ===== MOBILE: Two-image collage (visible < lg) ===== */}
            {templateType === 'LandingPage' && (
              <div className="flex justify-start mt-4 md:justify-center pl:8 lg:hidden">
                <div className="relative inline-block">
                  {/* Left image (bgImage) — taller */}
                  <div className="overflow-hidden rounded-2xl w-[190px] h-[260px]">
                    <ResponsiveImage
                      src={active.bgImage.src}
                      alt={active.nameAndTitle}
                      className="object-cover w-full h-full"
                    />
                  </div>
                  {/* Right image (cardImage) — shorter, vertically centered */}
                  <div className="absolute overflow-hidden shadow-lg left-[140px] top-1/2 -translate-y-1/2 rounded-2xl w-[190px] h-[200px]">
                    <ResponsiveImage
                      src={active.cardImage.src}
                      alt={active.nameAndTitle}
                      className="object-cover w-full h-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ===== DESKTOP: Image card (visible >= lg) ===== */}
            {templateType === 'SubPage' && subPageImages ? (
              <div className="flex justify-start md:justify-center pl:8 lg:justify-center lg:flex-1">
                <div className="relative inline-block">
                  {/* Primary (large) */}
                  <div className="overflow-hidden rounded-2xl w-[230px] h-[280px] md:w-[300px] md:h-[222px] lg:w-[364px] lg:h-[269px]">
                    <ResponsiveImage
                      src={subPageImages.primary.src}
                      alt={active.nameAndTitle}
                      className="object-cover w-full h-full"
                    />
                  </div>

                  {/* Secondary (small overlap) */}
                  <div className="absolute overflow-hidden shadow-lg rounded-2xl w-[180px] h-[150px] md:w-[160px] md:h-[134px] lg:w-[190px] lg:h-[159px] -right-[110px] top-[40px] md:-right-[90px] md:top-[50px] lg:-right-[108px] lg:top-[58px]">
                    <ResponsiveImage
                      src={subPageImages.secondary.src}
                      alt={active.nameAndTitle}
                      className="object-cover w-full h-full"
                    />
                  </div>
                </div>
              </div>
            ) : (
              /* Spacer for the left column on desktop — card is absolutely positioned above */
              <div className="hidden lg:block lg:flex-1" />
            )}

            {/* Quote content */}
            <div className="text-white lg:flex-1">
              <ResponsiveImage src={CommaOpen.src} alt="" className="object-contain w-auto h-6" />

              <Heading
                variant={EHeadingStyleVariant.H3}
                className="mt-2 mb-4 text-xl leading-snug text-black lg:text-3xl">
                {active.quote}
              </Heading>

              <Text
                variant={ETextStyleVariant.SmallBold}
                className="mb-6 text-black uppercase text-[10px] lg:text-[12px] tracking-[0.18em]">
                {active.subtext}
              </Text>

              <div className="flex flex-col items-start justify-between mt-6 lg:flex-row lg:items-center lg:mt-8 gap-6">
                <div>
                  <div className="flex items-center mb-1 gap-3">
                    <ResponsiveImage
                      src={active.signature.src}
                      alt={active.nameAndTitle}
                      className="object-contain w-auto h-6"
                    />
                  </div>
                  <Text variant={ETextStyleVariant.MediumRegular} className="text-black">
                    {active.nameAndTitle}
                  </Text>
                </div>

                {templateType === 'LandingPage' && (
                  <div className="flex justify-end w-full mb-5 gap-3 shrink-0 lg:justify-start lg:w-auto">
                    <Button
                      variant={EButtonColorVariant.Secondary}
                      className="flex items-center justify-center w-10 h-10 text-gray-900 bg-white rounded-full  !p-0"
                      label={<GeneralCaretLeftIcon fontSize={20} />}
                      onClick={goPrev}
                      aria-label="Previous testimonial"
                    />
                    <Button
                      variant={EButtonColorVariant.Secondary}
                      className="flex items-center justify-center w-10 h-10 text-gray-900 bg-white rounded-full  !p-0"
                      label={<GeneralCaretRightIcon fontSize={20} />}
                      onClick={goNext}
                      aria-label="Next testimonial"
                    />
                  </div>
                )}
              </div>
            </div>
          </MaxWidth>
        </div>
      </article>
    </section>
  );
};
